package fr.labodoc.healthProfessional.webApp

import fr.labodoc.healthProfessional.webApp.data.model.HealthProfessionalUserModel
import fr.labodoc.healthProfessional.webApp.data.repository.UsersRepository
import fr.labodoc.healthProfessional.webApp.di.dataSourceModule
import fr.labodoc.healthProfessional.webApp.di.repositoryModule
import fr.labodoc.healthProfessional.webApp.di.viewModelModule
import fr.labodoc.healthProfessional.webApp.layouts.LabodocFooter
import fr.labodoc.healthProfessional.webApp.layouts.defaultLayout
import fr.labodoc.healthProfessional.webApp.pages.aboutUs.aboutUsPage
import fr.labodoc.healthProfessional.webApp.pages.contact.contactPage
import fr.labodoc.healthProfessional.webApp.pages.dashboard.dashboardPage
import fr.labodoc.healthProfessional.webApp.pages.home.homePage
import fr.labodoc.healthProfessional.webApp.pages.laboratories.laboratoriesPage
import fr.labodoc.healthProfessional.webApp.pages.learnedSocieties.learnedSocietiesPage
import fr.labodoc.healthProfessional.webApp.pages.login.loginPage
import fr.labodoc.healthProfessional.webApp.pages.medicines.medicinePage
import fr.labodoc.healthProfessional.webApp.pages.notFound.notFoundPage
import fr.labodoc.healthProfessional.webApp.pages.privacyPolicyPage
import fr.labodoc.healthProfessional.webApp.pages.profile.profileInformation.profileInformationPage
import fr.labodoc.healthProfessional.webApp.pages.profile.profileMedicalInterestsPage
import fr.labodoc.healthProfessional.webApp.pages.profile.profileNotificationsSettingsPage
import fr.labodoc.healthProfessional.webApp.pages.register.registerPage
import fr.labodoc.healthProfessional.webApp.utils.toMap
import fr.labodoc.webapp.pages.aboutUs.fAQPage
import fr.labodoc.webapp.pages.aboutUs.whoAreWePage
import io.github.aakira.napier.DebugAntilog
import io.github.aakira.napier.Napier
import io.kvision.*
import io.kvision.i18n.DefaultI18nManager
import io.kvision.i18n.I18n
import io.kvision.panel.root
import io.kvision.routing.Routing
import io.kvision.routing.Strategy
import io.kvision.state.MutableState
import io.kvision.state.ObservableValue
import io.kvision.state.bind
import io.kvision.utils.Object
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.asCoroutineDispatcher
import kotlinx.coroutines.launch
import org.koin.core.component.KoinComponent
import org.koin.core.component.inject
import org.koin.core.context.startKoin
import org.w3c.dom.INSTANT
import org.w3c.dom.ScrollBehavior
import org.w3c.dom.ScrollToOptions

class App : Application(), KoinComponent {
  companion object : KoinComponent {
    val scope = CoroutineScope(window.asCoroutineDispatcher())

    val routing: Routing = Routing.init("/", useHash = false, strategy = Strategy.ONE)

    val user: MutableState<HealthProfessionalUserModel?> = ObservableValue(null)
  }

  private val userRepository: UsersRepository by inject()

  private val currentPage: MutableState<Page?> = ObservableValue(null)

  init {
    require("./css/theme.css")

    Napier.base(DebugAntilog())

    startKoin {
      modules(
          dataSourceModule,
          repositoryModule,
          viewModelModule
      )
    }
  }

  override fun start() {
    I18n.manager =
      DefaultI18nManager(
        mapOf(
          "fr" to require("./i18n/messages-fr.json")
        )
      )
    I18n.language = "fr"

    routing
      .on(
        Page.Home.URL_PATTERN,
        {
          val params = (it.params as? Object).toMap()
          val registrationSuccessful = (params?.get(Page.Home.REGISTRATION_SUCCESSFUL_QUERY_KEY) as String?)?.toBooleanStrictOrNull()
          val registrationConfirmEmailToken = (params?.get(Page.Home.REGISTRATION_CONFIRM_EMAIL_TOKEN_QUERY_KEY) as String?)
          val applicationSuccessful = (params?.get(Page.Home.APPLICATION_SUCCESSFUL_QUERY_KEY) as String?)?.toBooleanStrictOrNull()
          val applicationConfirmEmailToken = (params?.get(Page.Home.APPLICATION_CONFIRM_EMAIL_TOKEN_QUERY_KEY) as String?)
          val resetPasswordToken = (params?.get(Page.Home.RESET_PASSWORD_TOKEN_QUERY_KEY) as String?)

          val popup = when {
            registrationSuccessful != null -> Page.Home.Popup.RegistrationSuccessful(registrationSuccessful)
            registrationConfirmEmailToken != null -> Page.Home.Popup.RegistrationConfirmEmail(registrationConfirmEmailToken)
            applicationSuccessful != null -> Page.Home.Popup.ApplicationSuccessful(applicationSuccessful)
            applicationConfirmEmailToken != null -> Page.Home.Popup.ApplicationConfirmEmail(applicationConfirmEmailToken)
            resetPasswordToken != null -> Page.Home.Popup.ResetPassword(resetPasswordToken)
            else -> null
          }

          currentPage.setState(Page.Home(popup))
        }
      )
      .on(
        Page.PrivacyPolicy.URL_PATTERN,
        {
          currentPage.setState(Page.PrivacyPolicy())
        }
      )
      .on(
        Page.Login.URL_PATTERN,
        {
          val params = (it.params as? Object).toMap()

          when (user.getState()) {
            is HealthProfessionalUserModel -> Page.Dashboard().url
            null -> currentPage.setState(Page.Login(params?.get(Page.Login.REDIRECT_TO_QUERY_KEY) as String?))
          }
        }
      )
      .on(
        Page.Logout.URL_PATTERN,
        { currentPage.setState(Page.Logout()) }
      )
      .on(
        Page.Register.URL_PATTERN,
        { currentPage.setState(Page.Register()) }
      )
      .on(
        Page.Contact.URL_PATTERN,
        { currentPage.setState(Page.Contact()) }
      )
      .on(
        Page.FAQ.URL_PATTERN,
        { currentPage.setState(Page.FAQ()) }
      )
      .on(
        Page.About.URL_PATTERN,
        { currentPage.setState(Page.About()) }
      )
      .on(
        Page.WhoAreWe.URL_PATTERN,
        { currentPage.setState(Page.WhoAreWe()) }
      )
      .on(
        Page.Dashboard.URL_PATTERN,
        {
          val params = (it.params as? Object).toMap()

          currentPage.setState(Page.Dashboard(params?.get(Page.Dashboard.ATC_CLASSIFICATION_CODE_QUERY_KEY) as String?))
        }
      )
      .on(
        Page.MedicineSheet.URL_PATTERN,
        { currentPage.setState(Page.MedicineSheet(it.data["id"] as String)) }
      )
      .on(
        Page.LaboratoriesList.URL_PATTERN,
        { currentPage.setState(Page.LaboratoriesList()) }
      )
      .on(
        Page.LearnedSocietiesList.URL_PATTERN,
        { currentPage.setState(Page.LearnedSocietiesList()) }
      )
      .on(
        Page.ProfileInformation.URL_PATTERN,
        { currentPage.setState(Page.ProfileInformation()) }
      )
      .on(
        Page.ProfileMedicalInterests.URL_PATTERN,
        { currentPage.setState(Page.ProfileMedicalInterests()) }
      )
      .on(
        Page.ProfileNotificationsSettings.URL_PATTERN,
        { currentPage.setState(Page.ProfileNotificationsSettings()) }
      )
      .notFound({ currentPage.setState(null) })
      .resolve()

    root("labodoc") {}.bind(currentPage) {
      scope.launch {
        val user = user.getState() ?: userRepository.getSelf().fold({ null }, { it })?.also { user.setState(it) }

        if (user == null && it is AuthenticatedPage)
          routing.navigate(Page.Login(it.url))

        when (it) {
          is Page.Home -> {
            homePage(it.popup)
            add(LabodocFooter())
          }
          is Page.PrivacyPolicy -> defaultLayout { privacyPolicyPage() }

          is Page.Login -> defaultLayout { loginPage(it.redirectTo) }
          is Page.Logout -> {
            scope.launch {
              userRepository.logout()
              App.user.setState(null)
              routing.navigate(Page.Home())
            }
          }

          is Page.Register -> defaultLayout { registerPage() }
          is Page.Contact -> defaultLayout { contactPage() }
          is Page.About -> defaultLayout { aboutUsPage() }
          is Page.WhoAreWe -> defaultLayout { whoAreWePage() }
          is Page.FAQ -> defaultLayout { fAQPage() }
          is Page.Dashboard -> defaultLayout { dashboardPage(it.atcClassificationCode) }
          is Page.MedicineSheet -> defaultLayout { medicinePage(it.medicineId) }
          is Page.LaboratoriesList -> defaultLayout { laboratoriesPage() }
          is Page.LearnedSocietiesList -> defaultLayout { learnedSocietiesPage() }
          is Page.ProfileInformation -> defaultLayout { profileInformationPage() }
          is Page.ProfileMedicalInterests -> defaultLayout { profileMedicalInterestsPage() }
          is Page.ProfileNotificationsSettings -> defaultLayout { profileNotificationsSettingsPage() }
          else -> defaultLayout { notFoundPage() }
        }

        window.scrollTo(
          options = ScrollToOptions(
            left = 0.0,
            top = 0.0,
            behavior = ScrollBehavior.INSTANT
          )
        )
      }
    }
  }
}

fun main() {
  startApplication(
    ::App,
    module.hot,
    DatetimeModule,
    CoreModule
  )
}
